import React from "react";
import { logCritical } from "../utils/errorLogger";
import { AlertTriangle } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    // Clear the chunk-reload flag on successful page load
    sessionStorage.removeItem("chunk-reload-attempted");
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Auto-reload once on chunk load errors (stale deploy)
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      /loading chunk/i.test(error?.message) ||
      /failed to fetch dynamically imported module/i.test(error?.message);

    if (isChunkError && !sessionStorage.getItem("chunk-reload-attempted")) {
      sessionStorage.setItem("chunk-reload-attempted", "1");
      window.location.reload();
      return;
    }

    logCritical(error, {
      componentStack: errorInfo?.componentStack,
      source: "ErrorBoundary",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 px-4">
          <div className="max-w-md w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/90 p-8 shadow-2xl text-center space-y-5">
            <div className="flex justify-center">
              <AlertTriangle className="w-14 h-14 text-amber-700 dark:text-amber-400" />
            </div>

            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-slate-50 tracking-tight">
              Something went wrong
            </h1>

            <p className="text-sm text-slate-500 dark:text-slate-400">
              An unexpected error occurred. Our team has been notified. You can try reloading the page or navigating back to the dashboard.
            </p>

            {this.state.error?.message && (
              <pre className="text-left text-xs bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-red-700 dark:text-red-300 overflow-x-auto max-h-32">
                {this.state.error.message}
              </pre>
            )}

            <div className="flex gap-3 justify-center pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={() => (window.location.href = "/admin/dashboard")}
                className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-emerald-500 text-slate-700 dark:text-slate-200 text-sm font-bold transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
